import { useCallback, useEffect, useMemo, useState } from "react";
import ImageTemplateServices from "../services/ImageTemplateServices";
import { LIVE_YANTRA_IMAGE_URL } from "./utils";

/**
 * Outlet-only yantra-image override shared across live and all-draw pages.
 *
 * The actual override map ({ [yantra_id]: image_filename }) lives in module
 * scope so every call to getYantraImageUrl(img, yantra_id) is an O(1) plain
 * object lookup — no prop drilling, no per-render React work. A tiny pub-sub
 * around a version counter lets mounted screens re-render exactly once when
 * the override is fetched, with no further cost on subsequent renders.
 *
 * Fetch policy: at most one HTTP call per mount of useOutletTemplateImages
 * (per user_id / role change). Admin edits to the template do not stream
 * back to live screens, which is the explicit behavior the product wants.
 */
let TEMPLATE_YANTRA_IMG_OVERRIDE = null;
let TEMPLATE_YANTRA_NAME_OVERRIDE = null;
let templateVersionTick = 0;
const versionListeners = new Set();

const notifyListeners = () => {
  versionListeners.forEach((fn) => {
    try {
      fn(templateVersionTick);
    } catch (_) {}
  });
};

export const setTemplateImageOverride = (imgMap, nameMap) => {
  if (imgMap && typeof imgMap === "object") {
    const cleaned = {};
    Object.keys(imgMap).forEach((k) => {
      if (imgMap[k]) cleaned[k] = imgMap[k];
    });
    TEMPLATE_YANTRA_IMG_OVERRIDE = Object.keys(cleaned).length ? cleaned : null;
  } else {
    TEMPLATE_YANTRA_IMG_OVERRIDE = null;
  }

  if (nameMap && typeof nameMap === "object") {
    const cleaned = {};
    Object.keys(nameMap).forEach((k) => {
      if (nameMap[k]) cleaned[k] = nameMap[k];
    });
    TEMPLATE_YANTRA_NAME_OVERRIDE = Object.keys(cleaned).length ? cleaned : null;
  } else {
    TEMPLATE_YANTRA_NAME_OVERRIDE = null;
  }

  templateVersionTick += 1;
  notifyListeners();
};

export const clearTemplateImageOverride = () => {
  setTemplateImageOverride(null, null);
};

export const hasTemplateImageOverride = () => TEMPLATE_YANTRA_IMG_OVERRIDE != null;

export const getYantraImageUrl = (img, yantraId) => {
  const map = TEMPLATE_YANTRA_IMG_OVERRIDE;
  if (map && yantraId != null && map[yantraId]) {
    return `${LIVE_YANTRA_IMAGE_URL}/${map[yantraId]}`;
  }
  return img
    ? `${LIVE_YANTRA_IMAGE_URL}/${img}`
    : `${LIVE_YANTRA_IMAGE_URL}/default_image.png`;
};

/**
 * Returns the template's custom yantra name for a given yantra_id, or falls
 * back to `defaultName` if no active template override exists for that id.
 */
export const getYantraName = (defaultName, yantraId) => {
  const map = TEMPLATE_YANTRA_NAME_OVERRIDE;
  if (map && yantraId != null && map[yantraId]) {
    return map[yantraId];
  }
  return defaultName || "";
};

/**
 * Outlet-only hook. For role_id === 3 it fetches the user's active assigned
 * template once and updates the module-level override. For any other role
 * (or logged-out state) it clears the override. Returns a `templateVersion`
 * number that consumers can include in useMemo deps so memoized URL lists
 * refresh once when the override resolves.
 *
 * Cleanup intentionally does NOT clear the override, so the same outlet user
 * can navigate between Outlet Live Session and All Draw without re-fetching
 * or losing the templated images mid-navigation.
 */
export const useOutletTemplateImages = (user) => {
  const api = useMemo(() => new ImageTemplateServices(), []);
  const [version, setVersion] = useState(templateVersionTick);

  useEffect(() => {
    const handle = (v) => setVersion(v);
    versionListeners.add(handle);
    return () => {
      versionListeners.delete(handle);
    };
  }, []);

  useEffect(() => {
    const isOutlet = user && Number(user.role_id) === 3;
    if (!isOutlet) {
      setTemplateImageOverride(null);
      return undefined;
    }
    const controller = new AbortController();
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getMyActiveImages({ signal: controller.signal });
        if (cancelled) return;
        const payload = res?.data;
        if (
          res?.isSuccess &&
          payload &&
          Array.isArray(payload.images) &&
          payload.images.length > 0
        ) {
          const imgMap = {};
          const nameMap = {};
          payload.images.forEach((row) => {
            if (row?.yantra_id != null) {
              if (row.image_filename) imgMap[row.yantra_id] = row.image_filename;
              if (row.yantra_name) nameMap[row.yantra_id] = row.yantra_name;
            }
          });
          setTemplateImageOverride(imgMap, nameMap);
        } else {
          setTemplateImageOverride(null, null);
        }
      } catch (_) {
        if (controller.signal.aborted) return;
        setTemplateImageOverride(null, null);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [user?.user_id, user?.role_id, api]);

  return version;
};

/**
 * Load active template for a specific user (e.g. mobile user who placed the bet).
 * Does not touch the module-level outlet override — safe for place-bet modals.
 */
export const useTemplateForUser = (targetUserId, enabled = true) => {
  const api = useMemo(() => new ImageTemplateServices(), []);
  const [maps, setMaps] = useState({ img: null, name: null });

  useEffect(() => {
    if (!enabled || !targetUserId) {
      setMaps({ img: null, name: null });
      return undefined;
    }
    const controller = new AbortController();
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getActiveImagesForUser(targetUserId, {
          signal: controller.signal,
        });
        if (cancelled) return;
        const payload = res?.data;
        if (
          res?.isSuccess &&
          payload &&
          Array.isArray(payload.images) &&
          payload.images.length > 0
        ) {
          const imgMap = {};
          const nameMap = {};
          payload.images.forEach((row) => {
            if (row?.yantra_id != null) {
              if (row.image_filename) imgMap[row.yantra_id] = row.image_filename;
              if (row.yantra_name) nameMap[row.yantra_id] = row.yantra_name;
            }
          });
          setMaps({ img: imgMap, name: nameMap });
        } else {
          setMaps({ img: null, name: null });
        }
      } catch (_) {
        if (!cancelled) setMaps({ img: null, name: null });
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [targetUserId, enabled, api]);

  const resolveImageUrl = useCallback(
    (img, yantraId) => {
      if (maps.img && yantraId != null && maps.img[yantraId]) {
        return `${LIVE_YANTRA_IMAGE_URL}/${maps.img[yantraId]}`;
      }
      return img
        ? `${LIVE_YANTRA_IMAGE_URL}/${img}`
        : `${LIVE_YANTRA_IMAGE_URL}/default_image.png`;
    },
    [maps.img],
  );

  const resolveName = useCallback(
    (defaultName, yantraId) => {
      if (maps.name && yantraId != null && maps.name[yantraId]) {
        return maps.name[yantraId];
      }
      return defaultName || "";
    },
    [maps.name],
  );

  return {
    getYantraImageUrl: resolveImageUrl,
    getYantraName: resolveName,
    hasTemplate: Boolean(maps.img || maps.name),
  };
};
